import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import saveAs from 'file-saver';
import { forkJoin, from } from 'rxjs';
import {
  CompanyVendor,
  CompanyVendorFile,
  Note,
  SignedDocument,
  User
} from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { CompanyVendorFileUploadModalComponent } from 'src/app/modals/company-vendor-file-upload-modal/company-vendor-file-upload-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { NoteEmailDisplayModalComponent } from 'src/app/modals/note-email-display-modal/note-email-display-modal.component';
import { NoteModalComponent } from 'src/app/modals/note-modal/note-modal.component';
import { SignedDocumentFileDownloadModalComponent } from 'src/app/modals/signed-document-file-download-modal/signed-document-file-download-modal.component';
import { SignedDocumentUploadModalComponent } from 'src/app/modals/signed-document-upload-modal/signed-document-upload-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { FileService } from 'src/app/services/file.service';
import { NoteService } from 'src/app/services/note.service';
import { SignedDocumentService } from 'src/app/services/signed-document.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-vendor-documents-notes',
  templateUrl: './vendor-documents-notes.component.html',
  styleUrl: './vendor-documents-notes.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorDocumentsNotesComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private FormBuilder: FormBuilder,
    private ModalService: NgbModal,
    private NoteService: NoteService,
    private FileService: FileService,
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService,
    private SignedDocumentService: SignedDocumentService,
    private AuthService: AuthService
  ) {
    this.LoggedInUser = this.AuthService.getUser();
    const showArchivedFilesControl =
      this.DocumentAndNotesForm.get('showArchivedFiles');
    const showSystemLogsControl =
      this.DocumentAndNotesForm.get('showSystemLogs');
    const showAccountingLogsControl =
      this.DocumentAndNotesForm.get('showAccountingLogs');

    showArchivedFilesControl?.valueChanges.subscribe({
      next: () => this.onShowArchived()
    });

    showSystemLogsControl?.valueChanges.subscribe({
      next: () => this.onShowSystemLogs()
    });

    showAccountingLogsControl?.valueChanges.subscribe({
      next: () => this.onShowAccountingLogs()
    });

    this.SignedDocumentActionsForm.get('showArchived')?.valueChanges.subscribe({
      next: (changes) => {
        if (changes) {
          Object.assign(this.SignedDocumentsSuppliedGridQueryData, {
            isArchived: { $in: [true, false] }
          });
        } else {
          Object.assign(this.SignedDocumentsSuppliedGridQueryData, {
            isArchived: false
          });
        }

        this.reloadSignedDocumentsGrid = true;
      }
    });
  }

  @Input() CompanyId?: number;
  @Input() VendorId: number | null = null;
  @Input() ExemptVendorId: number | null = null;
  @Input() ClientRegisteredVendorId: number | null = null;

  CompanyVendor?: CompanyVendor;
  LoggedInUser!: User;

  DocumentAndNotesForm: FormGroup = this.FormBuilder.group({
    showArchivedFiles: [false],
    showSystemLogs: [false],
    showAccountingLogs: [false]
  });

  ChangeLogSuppliedData!: GenericObject;

  ColumnIdToColumnActionsCompanyVendorGrid: GridActionColumnMap = {
    ['$actionColumn']: ['archive', 'download']
  };

  TooltipMap: {
    [key in Extract<GridActionColumnActions, 'archive' | 'download'>]: string;
  } = {
    archive: 'Archive',
    download: 'Download'
  };

  ColumnIdToColumnActionsNotesGrid: GridActionColumnMap = {
    ['$actionColumn']: ['view']
  };

  CompanyVendorFilesGridQuery: GenericObject = {
    attributes: ['isArchived'],
    File: {
      attributes: ['id', 'fileName', 'fileType']
    },
    order: [['createdAt', 'DESC']]
  };
  reloadCompanyVendorFilesGrid: boolean = false;

  NotesGridQuery: GenericObject = {
    attributes: ['id', 'EmailId'],
    communicationType: { $ne: 'Accounting Log' },
    order: [['createdAt', 'DESC']]
  };

  reloadNotesGridQuery: boolean = false;

  SignedDocumentActionsForm: FormGroup = this.FormBuilder.group({
    showArchived: [false]
  });

  SignedDocumentsSuppliedGridQueryData: GenericObject = {
    attributes: ['id', 'service', 'isArchived', 'createdAt', 'updatedAt'],
    isArchived: false
  };
  SignedDocumentColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'download']
  };
  reloadSignedDocumentsGrid: boolean = false;

  approvedAdminRoles: UserAccountRoles[] = [
    'ADMIN_ADMIN',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',
    'ADMIN_FRAUD_TEAM'
  ];

  isAdminUser(): boolean {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    return role ? new Set(this.approvedAdminRoles).has(role) : false;
  }

  calculatedColumnActions({
    rowData
  }: {
    rowData: CompanyVendorFile;
  }): GridActionColumnActions[] | null {
    const gridActionColumnActionArr: GridActionColumnActions[] = [
      'archive',
      'download'
    ];
    const { isArchived } = rowData;

    if (isArchived) {
      const index = gridActionColumnActionArr.findIndex(
        (action) => action === 'archive'
      );
      gridActionColumnActionArr.splice(index, 1);
    }

    return gridActionColumnActionArr.length > 0
      ? gridActionColumnActionArr
      : null;
  }

  calculatedSignedDocumentActions = ({
    rowData
  }: {
    rowData: SignedDocument;
  }): GridActionColumnActions[] | null => {
    const gridActionColumnActionArr: GridActionColumnActions[] = ['download'];
    const { service, isArchived } = rowData;

    if (this.isAdminUser()) {
      if (service === 'Manual Upload' && !isArchived) {
        gridActionColumnActionArr.unshift('edit');
      }

      if (isArchived) {
        gridActionColumnActionArr.push('unarchive');
      } else {
        gridActionColumnActionArr.push('archive');
      }
    }

    return gridActionColumnActionArr.length > 0
      ? gridActionColumnActionArr
      : null;
  };

  downloadSignedDocument(SignedDocument: SignedDocument) {
    const { id: SignedDocumentId } = SignedDocument;

    const { componentInstance } = this.ModalService.open(
      SignedDocumentFileDownloadModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    Object.assign(componentInstance, { SignedDocumentId });
  }

  // TODO remove after updates are successful in production.
  // downloadSignedDocument(SignedDocument: SignedDocument) {
  //   if (!this.CompanyVendor) {
  //     throw this.ToastService.addErrorToast(
  //       'Error',
  //       'Cannot download. No CompanyVendor Record found.',
  //       { returnError: true }
  //     );
  //   }

  //   const { CompanyId, VendorId, Vendor } = this.CompanyVendor;

  //   if (!Vendor) {
  //     throw this.ToastService.addErrorToast(
  //       'Error',
  //       'Cannot download. No Vendor Record found.',
  //       { returnError: true }
  //     );
  //   }

  //   const { legalName } = Vendor;

  //   this.FileService.getVendorAgreement(
  //     this.UtilityService.stringifyObjectsInQueryObject(
  //       Object.assign(SignedDocument, {
  //         CompanyId,
  //         VendorId,
  //         getSignedDoc: true
  //       })
  //     )
  //   ).subscribe({
  //     next: (response) => {
  //       const { type } = response;
  //       let vendorAgreementBinaryDataArray = null;

  //       if (type === 'RSign') {
  //         const { data, fileNames } = response;

  //         vendorAgreementBinaryDataArray = data
  //           .map((Base64Data) => window.atob(Base64Data))
  //           .forEach((data, index) => {
  //             const fileName = fileNames[index];
  //             const blob = new Blob([
  //               new Uint8Array(map(data, (char) => char.charCodeAt(0)))
  //             ]);

  //             saveAs(blob, fileName);
  //           });
  //       } else if (type === 'DocuSign') {
  //         const { data } = response;

  //         // Handle new DocuSign structure: data is array of {document, filename}
  //         data.forEach((docItem: { document: string; fileName: string }) => {
  //           const { document: docData, fileName } = docItem;
  //           const binaryData = window.atob(docData);
  //           const blob = new Blob([
  //             new Uint8Array(map(binaryData, (char) => char.charCodeAt(0)))
  //           ]);

  //           saveAs(blob, fileName || 'document.pdf');
  //         });
  //       } else if (type === 'Stored') {
  //         const { data } = response;
  //         const { data: BinaryData } = data;

  //         vendorAgreementBinaryDataArray = [
  //           map(new Uint8Array(BinaryData), (val) =>
  //             String.fromCharCode(val)
  //           ).join('')
  //         ].forEach((data, index) => {
  //           const fileName = 'VendorAgreement.pdf';
  //           const blob = new Blob([
  //             new Uint8Array(map(data, (char) => char.charCodeAt(0)))
  //           ]);

  //           saveAs(blob, fileName);
  //         });
  //       }
  //     }
  //   });
  // }

  signedDocumentsAction({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: SignedDocument;
  }) {
    const { id } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot complete action. Missing ID.',
        { returnError: true }
      );
    }

    if (action === 'edit') {
      this.editSignedDocument(rowData);
    }

    if (action === 'archive' || action === 'unarchive') {
      this.toggleArchiveSignedDocument(rowData);
    }

    if (action === 'download') {
      this.downloadSignedDocument(rowData);
    }
  }

  companyVendorFileGridAction({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: CompanyVendorFile;
  }) {
    const { id, File } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot complete action. No file ID found.',
        { returnError: true }
      );
    }

    if (action === 'archive') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'lg' }
      );

      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to archive this file?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.FileService.saveCompanyVendorFile({
              id,
              isArchived: true
            }).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully archived file.'
                );
                this.reloadCompanyVendorFilesGrid = true;
              }
            });
          }
        }
      });
    }

    if (action === 'download') {
      this.FileService.getCompanyVendorFile(
        this.UtilityService.stringifyObjectsInQueryObject({
          id,
          File: {}
        })
      ).subscribe({
        next: (CompanyVendorFile: CompanyVendorFile) => {
          const { File, id: CompanyVendorFileId } = CompanyVendorFile;

          if (!File) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing file data.',
              { returnError: true }
            );
          }

          const { filename, filepath } = File;

          if (!filepath) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No file path found.',
              { returnError: true }
            );
          }

          this.FileService.getCompanyVendorFileS3PresignedUrl({
            id: CompanyVendorFileId
          }).subscribe({
            next: ({ filename, presignedUrl }) => {
              if (presignedUrl) {
                saveAs(presignedUrl, filename);
              }
            }
          });
        }
      });
    }
  }

  notesGridAction({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: Note;
  }) {
    const { id, EmailId } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot complete action. No Note ID found.',
        { returnError: true }
      );
    }

    if (action === 'view') {
      if (EmailId) {
        const { componentInstance } = this.ModalService.open(
          NoteEmailDisplayModalComponent,
          { backdrop: 'static', size: 'lg' }
        );
        Object.assign(componentInstance, { NoteId: id });
      } else {
        const { componentInstance, result } = this.ModalService.open(
          NoteModalComponent,
          { centered: true, size: 'lg' }
        );

        Object.assign(componentInstance, { NoteId: id });

        from(result).subscribe({
          next: ({ Note }: { Note: Note }) => {
            if (Note) {
              this.NoteService.saveNote(Note).subscribe({
                next: () => {
                  this.ToastService.addSuccessToast(
                    'Success',
                    'Successfully saved note.'
                  );
                  this.reloadNotesGridQuery = true;
                }
              });
            }
          }
        });
      }
    }
  }

  onShowArchived() {
    const showArchivedFilesControl =
      this.DocumentAndNotesForm.get('showArchivedFiles');

    if (showArchivedFilesControl && showArchivedFilesControl.value) {
      Object.assign(this.CompanyVendorFilesGridQuery, {
        isArchived: { $in: [true, false] }
      });
    } else {
      Object.assign(this.CompanyVendorFilesGridQuery, { isArchived: false });
    }

    this.reloadCompanyVendorFilesGrid = true;
  }

  onShowSystemLogs() {
    const showSystemLogsControl =
      this.DocumentAndNotesForm.get('showSystemLogs');

    if (showSystemLogsControl && showSystemLogsControl.value) {
      Object.assign(this.NotesGridQuery, { userType: { $ne: 'SYSTEM' } });
    } else {
      delete this.NotesGridQuery['userType'];
    }

    this.reloadNotesGridQuery = true;
  }

  onShowAccountingLogs() {
    const showAccountingLogsControl =
      this.DocumentAndNotesForm.get('showAccountingLogs');

    if (showAccountingLogsControl && showAccountingLogsControl.value) {
      Object.assign(this.NotesGridQuery, {
        communicationType: 'Accounting Log'
      });
    } else {
      Object.assign(this.NotesGridQuery, {
        communicationType: { $ne: 'Accounting Log' }
      });
    }

    this.reloadNotesGridQuery = true;
  }

  createNote() {
    const NoteModal = this.ModalService.open(NoteModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'note-modal'
    });
    const { componentInstance } = NoteModal;
    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId
    });
    from(NoteModal.result).subscribe({
      next: ({ Note }: { Note: Note }) => {
        this.NoteService.saveNote(Note).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully saved note.'
            );
            this.reloadNotesGridQuery = true;
          }
        });
      }
    });
  }

  uploadFile() {
    const { componentInstance, result } = this.ModalService.open(
      CompanyVendorFileUploadModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    Object.assign(componentInstance, { CompanyVendor: this.CompanyVendor });

    from(result).subscribe({
      next: (Files: CompanyVendorFile[]) => {
        if (Files.length > 0) {
          forkJoin(
            Files.map((file) => this.FileService.saveCompanyVendorFile(file))
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully uploaded company vendor file.'
              );
              this.reloadCompanyVendorFilesGrid = true;
            }
          });
        }
      }
    });
  }

  uploadSignedDocument() {
    if (!this.isAdminUser()) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You do not have permission to upload signed documents.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      SignedDocumentUploadModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    Object.assign(componentInstance, { CompanyVendor: this.CompanyVendor });

    from(result).subscribe({
      next: (SignedDocuments: SignedDocument[]) => {
        if (SignedDocuments.length > 0) {
          forkJoin(
            SignedDocuments.map((signedDocument) =>
              this.SignedDocumentService.saveSignedDocument(signedDocument)
            )
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully uploaded signed documents.'
              );
              this.reloadSignedDocumentsGrid = true;
            }
          });
        }
      }
    });
  }

  toggleArchiveSignedDocument(signedDocument: SignedDocument) {
    if (!this.isAdminUser()) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You do not have permission to modify signed documents.',
        { returnError: true }
      );
    }

    const { isArchived } = signedDocument;
    const newArchivedState = !isArchived;
    const action = newArchivedState ? 'archive' : 'unarchive';

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'lg' }
    );

    const displayText = `Are you sure you want to ${action} this signed document?`;

    Object.assign(componentInstance, {
      displayText
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          const updatedSignedDocument = {
            ...signedDocument,
            isArchived: newArchivedState
          };

          this.SignedDocumentService.saveSignedDocument(
            updatedSignedDocument
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                `Successfully ${action}d signed document.`
              );
              this.reloadSignedDocumentsGrid = true;
            }
          });
        }
      }
    });
  }

  editSignedDocument(signedDocument: SignedDocument) {
    if (!this.isAdminUser()) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You do not have permission to edit signed documents.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      SignedDocumentUploadModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    Object.assign(componentInstance, {
      CompanyVendor: this.CompanyVendor,
      existingSignedDocument: signedDocument
    });

    from(result).subscribe({
      next: (SignedDocuments: SignedDocument[]) => {
        if (SignedDocuments.length > 0) {
          forkJoin(
            SignedDocuments.map((signedDocument) =>
              this.SignedDocumentService.saveSignedDocument(signedDocument)
            )
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully updated signed documents.'
              );
              this.reloadSignedDocumentsGrid = true;
            }
          });
        }
      }
    });
  }

  loadCompanyVendor() {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        if (!this.CompanyId) {
          this.CompanyId = Number(paramMap.get('CompanyId'));
        }

        if (!this.VendorId) {
          this.VendorId = paramMap.get('VendorId')
            ? Number(paramMap.get('VendorId'))
            : null;
        }

        if (!this.ExemptVendorId) {
          this.ExemptVendorId = paramMap.get('ExemptVendorId')
            ? Number(paramMap.get('ExemptVendorId'))
            : null;
        }

        if (!this.ClientRegisteredVendorId) {
          this.ClientRegisteredVendorId = paramMap.get(
            'ClientRegisteredVendorId'
          )
            ? Number(paramMap.get('ClientRegisteredVendorId'))
            : null;
        }

        const CompanyVendorId = Number(paramMap.get('CompanyVendorId'));

        if (!CompanyVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor id.',
            { returnError: true }
          );
        }

        if (
          !this.CompanyId ||
          (!this.VendorId &&
            !this.ClientRegisteredVendorId &&
            !this.ExemptVendorId)
        ) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot load documents and notes. Missing vendor information.',
            { returnError: true }
          );
        }

        const CompanyVendorQuery = {
          CompanyId: this.CompanyId,
          VendorId: this.VendorId,
          ClientRegisteredVendorId: this.ClientRegisteredVendorId,
          ExemptVendorId: this.ExemptVendorId,
          Vendor: {
            options: { required: false },
            attributes: ['id', 'legalName']
          },
          ExemptVendor: {
            options: { required: false },
            attributes: ['id', 'legalName']
          },
          ClientRegisteredVendor: {
            options: { required: false },
            attributes: ['id', 'legalName']
          },
          Company: {
            attributes: ['id', 'legalName']
          }
        };

        const NoteQueryObject: GenericObject = {
          isChangeLog: false,
          CompanyId: this.CompanyId,
          VendorId: this.VendorId || null,
          ExemptVendorId: this.ExemptVendorId || null,
          ClientRegisteredVendorId: this.ClientRegisteredVendorId || null
        };

        this.CompanyVendorService.getCompanyVendor(
          this.UtilityService.stringifyObjectsInQueryObject(CompanyVendorQuery)
        ).subscribe({
          next: (CompanyVendor: CompanyVendor) =>
            (this.CompanyVendor = CompanyVendor)
        });

        const CompanyVendorFilesGridQuery: GenericObject = { CompanyVendorId };

        if (this.VendorId) {
          Object.assign(NoteQueryObject, {
            Vendor: {
              options: { required: false },
              attributes: ['id', 'legalName']
            }
          });
        }

        if (this.ExemptVendorId) {
          Object.assign(NoteQueryObject, {
            ExemptVendor: {
              options: { required: false },
              attributes: ['id', 'legalName']
            }
          });
        }

        if (this.ClientRegisteredVendorId) {
          Object.assign(NoteQueryObject, {
            ClientRegisteredVendor: {
              options: { required: false },
              attributes: ['id', 'legalName']
            }
          });
        }

        Object.assign(this.NotesGridQuery, NoteQueryObject);
        Object.assign(
          this.CompanyVendorFilesGridQuery,
          CompanyVendorFilesGridQuery
        );
        Object.assign(
          this.SignedDocumentsSuppliedGridQueryData,
          CompanyVendorQuery
        );
        this.ChangeLogSuppliedData = CompanyVendorQuery;
      }
    });
  }

  ngOnInit(): void {
    this.loadCompanyVendor();
  }
}
